import { cn } from './cn'

/**
 * 按钮的外观。**只有 class 字符串，没有组件**——
 * 因为「长得像按钮的链接」（导航用 `<a>`，不是 `<button>`）要用同一份外观，
 * 而它不能靠 `Button` 渲染出来。放在同一个文件里会让 Vite 的热更失去组件边界。
 *
 * 三个外观来自设计稿：
 *   primary   深绿实底。一屏里只该有一个
 *   secondary 白底描边。默认选它
 *   danger    红色。碰钱、碰数据、删东西才用
 *
 * 危险动作的按钮长成红色**不代表点了就会执行**——
 * 红级动作永远要走二次验证，那是接口层的事，不是这里能保证的。
 */
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost'
export type ButtonSize = 'md' | 'sm'

const base =
  'inline-flex items-center justify-center gap-npc-gap-xs font-[inherit] whitespace-nowrap ' +
  'cursor-pointer transition-colors duration-npc-hover ' +
  'disabled:cursor-not-allowed disabled:opacity-50'

const sizes: Record<ButtonSize, string> = {
  md: 'px-npc-button-x py-npc-button-y text-npc-body rounded-npc-button',
  sm: 'px-npc-button-sm-x py-npc-button-sm-y text-npc-body-sm rounded-npc-code',
}

const variants: Record<ButtonVariant, string> = {
  primary: 'border-none bg-npc-green text-npc-panel hover:bg-npc-green-hover',
  secondary: 'border border-npc-border bg-npc-panel text-npc-ink hover:border-npc-border-hover',
  danger: 'border border-npc-red-border bg-npc-red-bg text-npc-red-ink hover:bg-npc-red-bg',
  ghost: 'border-none bg-transparent text-npc-ink-medium hover:bg-npc-surface',
}

/**
 * 拼出按钮的 class。
 *
 * 「+ 加一台机器」是导航（去另一屏）而不是提交，所以它该是 `<a>`——
 * 中键新开标签页、右键复制地址都得能用。外观与 `Button` 走同一份 class，
 * 不复制一份出来慢慢走偏。
 */
export function buttonClass({
  variant = 'secondary',
  size = 'md',
  className,
}: {
  variant?: ButtonVariant
  size?: ButtonSize
  className?: string
} = {}): string {
  return cn(base, sizes[size], variants[variant], className)
}
